/*
  The one TCG / Sports choice the whole site shares (docs/specs/pop-report-batch3.md D2).

  The header's Games menu and the home page's Browse by Game both call this, and they are
  in different subtrees, so the value lives in this module rather than in either one: a
  plain store with a listener set. Flipping it in the menu changes the home page at once,
  and the other way round.

  It is read from storage once, on first use. Only `choose` writes, and only the visitor's
  click calls it (D3).

  Lives here beside useLanguageBucket rather than in lib/, whose modules touch no React.
*/

import { useCallback, useEffect, useState } from "react";

import { readSavedCategory, saveCategory } from "lib";
import type { HomeCategory } from "lib";

let current: HomeCategory | null = null;
const listeners = new Set<(category: HomeCategory) => void>();

const read = (): HomeCategory => {
  if (current === null) {
    current = readSavedCategory();
  }
  return current;
};

export const useHomeCategory = (): [HomeCategory, (category: HomeCategory) => void] => {
  const [category, setCategory] = useState<HomeCategory>(read);

  useEffect(() => {
    listeners.add(setCategory);
    /* Catch a change made between this component's render and its effect. */
    setCategory(read());
    return () => {
      listeners.delete(setCategory);
    };
  }, []);

  const choose = useCallback((next: HomeCategory) => {
    current = next;
    saveCategory(next);
    listeners.forEach(listener => listener(next));
  }, []);

  return [category, choose];
};
